import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { FaArrowLeft, FaPlus, FaTrash, FaGift } from 'react-icons/fa6';
import { fetchJSON } from '../lib/api';

const COURSE_SLUG = 'ai-for-parents';
const PRICE_PER = 'CA$14.99';

const stripePromise = fetch('/api/checkout/config')
  .then((r) => r.json())
  .then(({ publishableKey }) => loadStripe(publishableKey));

function emptyRecipient() {
  return { email: '', firstName: '', lastName: '' };
}

export default function GiftCourse() {
  const [recipients, setRecipients] = useState([emptyRecipient()]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);

  const update = (idx, field, value) => {
    setRecipients((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const addRecipient = () => {
    if (recipients.length < 5) setRecipients((prev) => [...prev, emptyRecipient()]);
  };

  const removeRecipient = (idx) => {
    setRecipients((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    for (const r of recipients) {
      if (!r.email.trim() || !r.firstName.trim() || !r.lastName.trim()) {
        setError('Please fill in all fields for every recipient.');
        return;
      }
    }
    const emails = recipients.map((r) => r.email.trim().toLowerCase());
    const uniqueEmails = new Set(emails);
    if (uniqueEmails.size !== emails.length) {
      setError('Each recipient must have a unique email address — please remove any duplicates.');
      return;
    }

    setSubmitting(true);
    try {
      const data = await fetchJSON('/api/gifts/embedded-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipients }),
      });
      if (data.clientSecret) {
        setClientSecret(data.clientSecret);
      } else {
        setError(data.error || 'Something went wrong. Please try again.');
        setSubmitting(false);
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  const fetchClientSecret = useCallback(() => Promise.resolve(clientSecret), [clientSecret]);

  const totalStr = recipients.length === 1
    ? PRICE_PER
    : `${recipients.length} × ${PRICE_PER} = CA$${(recipients.length * 14.99).toFixed(2)}`;

  return (
    <div className="w-full bg-bone min-h-screen">
      <div className="border-b border-ink/10 bg-bone">
        <div className="container-page py-4">
          <Link
            to={`/courses/${COURSE_SLUG}`}
            className="inline-flex items-center gap-2 text-sm text-slate2 hover:text-ink"
          >
            <FaArrowLeft className="text-[10px]" />
            <span className="font-display text-base">Generative AI for Parents</span>
          </Link>
        </div>
      </div>

      <div className="container-page py-12 lg:py-16 max-w-2xl mx-auto">
        {/* Step 1: Recipient form */}
        {!clientSecret && (
          <>
            <div className="flex items-center gap-3 mb-2">
              <FaGift className="text-brand-aubergine text-xl" />
              <div className="eyebrow">Gift this course</div>
            </div>
            <h1 className="font-display text-3xl lg:text-4xl leading-tight text-ink">
              Give someone the knowledge they need
            </h1>
            <p className="mt-4 text-slate2 leading-relaxed">
              Each recipient receives their own full lifetime access. Upon completing the course and
              demonstrating their developed skills, they can earn a certificate of completion —
              their name is set automatically from the details you provide here.
            </p>

            <form onSubmit={handleSubmit} className="mt-10 space-y-6">
              {recipients.map((r, idx) => (
                <div key={idx} className="card-soft p-6 relative">
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-[11px] tracking-[0.18em] uppercase text-brand-deep">
                      {recipients.length > 1 ? `Recipient ${idx + 1}` : 'Recipient'}
                    </div>
                    {recipients.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeRecipient(idx)}
                        className="text-slate2 hover:text-red-600 transition-colors p-1"
                        aria-label="Remove recipient"
                      >
                        <FaTrash className="text-xs" />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-slate2 mb-1" htmlFor={`fn-${idx}`}>
                        First name
                      </label>
                      <input
                        id={`fn-${idx}`}
                        type="text"
                        required
                        autoComplete="off"
                        value={r.firstName}
                        onChange={(e) => update(idx, 'firstName', e.target.value)}
                        placeholder="Alex"
                        className="w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm text-ink focus:outline-none focus:border-brand-deep/60 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate2 mb-1" htmlFor={`ln-${idx}`}>
                        Last name
                      </label>
                      <input
                        id={`ln-${idx}`}
                        type="text"
                        required
                        autoComplete="off"
                        value={r.lastName}
                        onChange={(e) => update(idx, 'lastName', e.target.value)}
                        placeholder="Morgan"
                        className="w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm text-ink focus:outline-none focus:border-brand-deep/60 transition-colors"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-xs text-slate2 mb-1" htmlFor={`em-${idx}`}>
                      Email address
                    </label>
                    <input
                      id={`em-${idx}`}
                      type="email"
                      required
                      autoComplete="off"
                      value={r.email}
                      onChange={(e) => update(idx, 'email', e.target.value)}
                      placeholder="alex@example.com"
                      className="w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm text-ink focus:outline-none focus:border-brand-deep/60 transition-colors"
                    />
                    <p className="mt-1.5 text-[11px] text-slate2">
                      Their sign-in link and course access will be sent here.
                    </p>
                  </div>
                </div>
              ))}

              {recipients.length < 5 && (
                <button
                  type="button"
                  onClick={addRecipient}
                  className="inline-flex items-center gap-2 text-sm text-brand-deep hover:text-ink transition-colors"
                >
                  <FaPlus className="text-[10px]" />
                  Add another recipient
                </button>
              )}

              {error && (
                <div className="rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm px-5 py-4">
                  {error}
                </div>
              )}

              <div className="border-t border-ink/10 pt-6">
                <div className="mb-5">
                  <div className="text-[11px] tracking-[0.18em] uppercase text-brand-aubergine">
                    Order total
                  </div>
                  <div className="mt-1 font-display text-xl text-ink">{totalStr}</div>
                  <div className="text-xs text-slate2 mt-0.5">
                    One-time · Lifetime access for the recipient
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full sm:w-auto disabled:opacity-60 disabled:cursor-wait inline-flex justify-center"
                >
                  {submitting ? 'Loading payment form…' : 'Continue to payment'}
                </button>
                <p className="mt-3 text-xs text-slate2">
                  Secure payment via Stripe
                </p>
              </div>
            </form>
          </>
        )}

        {/* Step 2: Embedded Stripe checkout */}
        {clientSecret && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <FaGift className="text-brand-aubergine text-xl" />
              <div className="eyebrow">Gift this course — secure checkout</div>
            </div>
            <div className="rounded-2xl overflow-hidden border border-ink/10 bg-white shadow-soft">
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={{ fetchClientSecret }}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
            <p className="mt-6 text-center text-xs text-slate2">
              Payment processed securely by{' '}
              <span className="font-medium text-ink">Stripe</span>.
              Your card details never touch our servers.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
